import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { ImageConverterProvider } from '@services/files/image-converter.service';
import { MessageService } from '@services/helpers/message-service/message.service';

@Injectable()
export class FileUploadServiceProvider {
	maxMb = 20; // 20 Mb
	maxSize: number = this.maxMb * 1000 * 1024;
	maxAttachment = 10; // 10 Mb
	maxClientPhoto = 5; // 5 Mb

	constructor(
		private imageConverter: ImageConverterProvider,
		private messageService: MessageService
	) {}

	addFileConvertedSize(attachment) {
		attachment.convertedType = this.imageConverter.getFileClass(
			attachment.name
		);
		attachment.convertedSize = this.getConvertedSize(attachment);
		return attachment;
	}

	// used in web app only
	removeDuplicates(array, keyname) {
		const output: any = [];
		const keys: any = [];

		array.forEach(item => {
			const key = item[keyname];
			if (!keys.includes(key) || !key) {
				keys.push(key);
				output.push(item);
			}
		});
		return output;
	}

	getConvertedSize(attachment): number {
		let convertedSize;
		if (attachment.bytesSize) {
			convertedSize = (parseInt(attachment.bytesSize) / (1024 * 1024)).toFixed(
				2
			); // convert from bytes to MB
		} else {
			convertedSize = (parseInt(attachment.size) / (1024 * 1024)).toFixed(2); // convert from bytes to MB
		}

		return Number(convertedSize);
	}

	isFileExceedLimit = (file): boolean => file.size >= this.maxSize;

	isPhotoExceedLimit = (photo): boolean => {
		const sizeInMb = this.getConvertedSize(photo);
		return sizeInMb >= this.maxClientPhoto;
	};

	isValidPhotoFormat = (photo): boolean =>
		/\.(gif|jpg|jpeg|png)$/i.test(photo.name);

	isAllFilesExceedLimit(file, allFiles): boolean {
		if (this.getTotalSize(allFiles) + file.size > this.maxSize) {
			return true;
		}
		return false;
	}

	getAllAttachmentsSize(
		attachmentsSize = 0,
		newAttachments: Array<any>
	): number {
		return attachmentsSize + this.getTotalSize(newAttachments);
	}

	deleteFilesFromTasks(tasks, file) {
		return tasks.map(task => {
			const hasAttachments =
				task.taskEmail &&
				task.taskEmail.storedAttachments &&
				task.taskEmail.storedAttachments.length;
			if (hasAttachments) {
				const { storedAttachments } = task.taskEmail;
				const deletedFileId = file.id;
				storedAttachments.forEach((attachment, index) => {
					if (attachment.id === deletedFileId) {
						storedAttachments.splice(index, 1);
					}
				});
			}
			return task;
		});
	}

	convertFileToBase64 = file =>
		this.readFile(file).map(result => {
			if (result) {
				const metaDataEnd = result.indexOf(',') + 1;
				const convertedFile = result.slice(metaDataEnd).trim();
				return {
					name: file.name,
					contentType: file.type,
					size: file.size,
					base64: convertedFile
				};
			}
			console.error('error in file reader');
			return null;
		});

	getTotalSize(files): number {
		return files.reduce((prev, next) => {
			const nextSize = this.getConvertedSize(next);
			return prev + nextSize;
		}, 0);
	}

	isClientPhotoValid(photo): boolean {
		if (!this.isValidPhotoFormat(photo)) {
			this.messageService.showToaster(
				'e_2',
				'general',
				'Please upload an image'
			);
			return false;
		}

		if (this.isPhotoExceedLimit(photo)) {
			this.messageService.showToaster('e_2', 'companyInformation');
			return false;
		}

		return true;
	}

	convertFileToSave(filesArr) {
		const result = filesArr.map(file => this.convertFileToBase64(file));
		return Observable.zip(...result);
	}

	convertAttachmentsToShow(attachments) {
		const attachmentsToShow = attachments.slice();

		attachmentsToShow.forEach(attachment => {
			attachment.customLastModifiedDate = this.getCustomLastModifiedDate(
				attachment
			);
			attachment.convertedSize = this.getConvertedSize(attachment);
			attachment.convertedType = this.imageConverter.getFileClass(
				attachment.name
			);
		});

		return attachmentsToShow;
	}

	getCustomLastModifiedDate(attachment) {
		const monthNames = [
			'January',
			'February',
			'March',
			'April',
			'May',
			'June',
			'July',
			'August',
			'September',
			'October',
			'November',
			'December'
		];
		const tmpDate = new Date(attachment.cratedTs);
		return `${tmpDate.getDate()} ${
			monthNames[tmpDate.getMonth()]
		} ${tmpDate.getFullYear()}`;
	}

	base64StringToObject = (img: string) => {
		if (!img || typeof img !== 'string') {
			return;
		}

		const imgStrToArray = img.split(',');
		const base64content = imgStrToArray[1].trim();
		const dataPartLength = 'data:'.length;
		const contentType = imgStrToArray[0].split(';')[0].substr(dataPartLength);

		return {
			base64content,
			contentType
		};
	};

	objectToBase64String(photo): string {
		const { base64content, contentType } = photo;
		if (!base64content || !contentType) {
			return 'none';
		}
		return `url(data:${contentType};base64,${base64content})`;
	}

	private readFile = blob =>
		Observable.create(obs => {
			if (!(blob instanceof Blob)) {
				obs.error(new Error('`blob` must be an instance of File or Blob.'));
				return;
			}

			const reader = new FileReader();

			reader.onerror = err => obs.error(err);
			reader.onabort = err => obs.error(err);
			reader.onload = () => obs.next(reader.result);
			reader.onloadend = () => obs.complete();

			return reader.readAsDataURL(blob);
		});
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=kkml25e9zf!./shared/services/files/file-upload-service/file-upload.service.ts